import { atom, atomFamily, DefaultValue, selectorFamily, useRecoilValue } from 'recoil';
import type { RecoilState } from 'recoil';

import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';

import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useMatch } from 'react-router-dom';
import { AppRoutePath } from 'config/constants';

/**
 * Viewports that have been expanded (either full screen or split).
 *
 * It will be considered 'up'
 */

export const viewportUpIds: RecoilState<{
  [key: string]: Nullable<Array<string>>;
}> = atom<{
  [key: string]: Nullable<Array<string>>;
}>({
  key: 'viewer.dre.viewportUpIds',
  default: {},
  effects: [broadcastChannelSynchronizerEffect()],
});

export const viewportUpIdSelector: (
  windowId?: Nullable<string>
) => RecoilState<ReadonlyArray<string>> = selectorFamily({
  key: 'viewer.dre.viewportUpIdsSelector',
  get:
    (windowId?: string | null) =>
    ({ get }) => {
      if (windowId == null) {
        return [];
      }
      return get(viewportUpIds)[windowId] ?? [];
    },
  set:
    (windowId?: string | null) =>
    ({ set, get }, newValue: DefaultValue | ReadonlyArray<string>) => {
      if (windowId == null) {
        return;
      }

      const current = get(viewportUpIds);
      // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'RecoilState<{ [key: string]: string[] | null | undefined; }>' is not assignable to parameter of type 'RecoilState<{ [x: string]: string[] | readonly string[] | DefaultValue | null | undefined; }>'.
      set(viewportUpIds, {
        ...current,
        [windowId]: newValue,
      });
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const selectedThumbnailState: RecoilState<Nullable<string>> = atom<Nullable<string>>({
  key: 'selectedThumbnail',
  default: null,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const baseViewerDataLoadingState: RecoilState<boolean> = atom({
  key: 'baseViewerDataLoadingState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const thumbnailBarVisible: (caseSmid: string) => RecoilState<boolean> = atomFamily({
  key: 'thumbnailBarVisibile',
  default: true,
  effects: [broadcastChannelSynchronizerEffect()],
});

// Clicked prior-study chip smids; broadcast-synced so a click in one viewer window blocks re-clicks in the other.
export const clickedChipSmidsState: (caseSmid: string) => RecoilState<ReadonlyArray<string>> =
  atomFamily<ReadonlyArray<string>, string>({
    key: 'clickedChipSmids',
    default: [],
    effects: [broadcastChannelSynchronizerEffect()],
  });

// Count of prior studies currently loaded in a Viewer window for this case; broadcast-synced so
// Reporter (a separate window/Apollo cache) can read it without fetching viewer data itself. Null
// means no Viewer window has reported a count yet for this case.
export const viewerPriorStudyCountState: (caseSmid: string) => RecoilState<Nullable<number>> =
  atomFamily<Nullable<number>, string>({
    key: 'viewerPriorStudyCount',
    default: null,
    effects: [broadcastChannelSynchronizerEffect()],
  });

export const toolBarVisible: RecoilState<boolean> = atom({
  key: 'toolBarVisible',
  default: true,
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

export function useThumbnailBarVisible(): boolean {
  const currentCaseSmid = useCurrentCaseId();
  return useRecoilValue(thumbnailBarVisible(currentCaseSmid ?? ''));
}

export function useSidebarVisible(): boolean {
  const showToolBar = useRecoilValue(toolBarVisible);
  const isViewer = useMatch(AppRoutePath.VIEWER) != null;
  return !isViewer || showToolBar;
}
